import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import { translateOptional } from "@/i18n/translate";
import { opSymbol, percentDigits } from "./model";
import type { EvaluationSetting, SettingValue } from "./types";

/**
 * Names, descriptions and value formats of the settings in the UI language. Backend keys without a
 * translation (a setting added later) fall back to the raw key, so the page never hides one.
 */
export function useSettingText(settings: EvaluationSetting[] | undefined) {
  const { locale, t, formatNumber } = useLocalization();

  return useMemo(() => {
    const opt = (key: string, values?: Record<string, string | number>) => translateOptional(key, values, locale);
    const byKey = new Map((settings ?? []).map((s) => [s.key, s]));
    const enforceKeys = new Set((settings ?? []).map((s) => s.enforce_key).filter(Boolean) as string[]);

    const label = (key: string) => opt(`evalSettings.key.${key}.label`) ?? key;
    const description = (key: string) => opt(`evalSettings.key.${key}.description`);
    const groupLabel = (group: string) => opt(`evalSettings.group.${group}`) ?? group;
    const groupDescription = (group: string) => opt(`evalSettings.group.${group}.description`);
    /** what firing means in a group ("Invalid photo", "NOK", "On a plate") */
    const groupFires = (group: string) => opt(`evalSettings.group.${group}.fires`) ?? t("evalSettings.group.fires");
    const metricLabel = (metric: string) => opt(`evalSettings.metric.${metric}`) ?? metric;
    const metricEmpty = (metric: string) => opt(`evalSettings.metric.${metric}.empty`);

    /** a ratio in % with the precision of its step: 35 %, 0.5 %, 2.25 % */
    const percent = (value: number, step: number | null | undefined) =>
      formatNumber(value, { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: percentDigits(step) });

    const flagText = (key: string, value: boolean) =>
      enforceKeys.has(key)
        ? value
          ? t("evalSettings.enforce.on")
          : t("evalSettings.enforce.off")
        : value
          ? t("evalSettings.flag.on")
          : t("evalSettings.flag.off");

    /** a value of `key` as shown to people (settings unknown to this page keep their raw number) */
    const value = (key: string, v: SettingValue | null | undefined): string => {
      if (v === null || v === undefined) return "–";
      if (typeof v === "boolean") return flagText(key, v);
      const setting = byKey.get(key);
      if (!setting || setting.unit === "probability" || setting.unit === "ratio") {
        return setting || (v >= 0 && v <= 1) ? percent(v, setting?.step ?? 0.0001) : formatNumber(v);
      }
      return formatNumber(v, { maximumFractionDigits: 4 });
    };

    /** "p(blurred) ≥ 30 %" */
    const rule = (setting: EvaluationSetting, v: number) =>
      `${setting.metric ? metricLabel(setting.metric) : label(setting.key)} ${opSymbol(setting.fail_when)} ${value(setting.key, v)}`;

    /** "≥ 30 %" */
    const shortRule = (setting: EvaluationSetting, v: number) => `${opSymbol(setting.fail_when)} ${value(setting.key, v)}`;

    return {
      label,
      description,
      groupLabel,
      groupDescription,
      groupFires,
      metricLabel,
      metricEmpty,
      percent,
      value,
      rule,
      shortRule,
      isEnforceKey: (key: string) => enforceKeys.has(key),
    };
  }, [settings, locale, t, formatNumber]);
}

export type SettingText = ReturnType<typeof useSettingText>;
